// src/pages/portal/HistorialRecargas.js
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { supabase } from '../../supabaseClient';
import { useAuth } from '../../AuthContext';
import { Loader2, TrendingUp, AlertTriangle } from 'lucide-react';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";

// --- Sub-componente para historial de Estudiante/Padre ---
const StudentRechargeHistory = () => {
    const { userData, userType, loading: authLoading } = useAuth();
    // *** CORRECCIÓN DE SINTAXIS AQUÍ ***
    const [students, setStudents] = useState([]); // Faltaba el '='
    // *** FIN CORRECCIÓN ***
    const [selectedStudentId, setSelectedStudentId] = useState('');

    const [recharges, setRecharges] = useState([]);
    const [loading, setLoading] = useState(false);
    const [startDate, setStartDate] = useState(new Date(new Date().setDate(new Date().getDate() - 30)));
    const [endDate, setEndDate] = useState(new Date());

    // Configura el selector de estudiantes
    useEffect(() => {
        if (userType === 'parent' && userData?.students) {
            setStudents(userData.students);
            if (userData.students.length > 0) setSelectedStudentId(userData.students[0].id);
        } else if (userType === 'student' && userData) {
            setStudents([userData]);
            setSelectedStudentId(userData.id);
        }
    }, [userData, userType]);

    // Carga los datos cuando cambia el estudiante o las fechas
    const fetchData = useCallback(async () => {
        if (!selectedStudentId) {
            setRecharges([]);
            return;
        }
        setLoading(true);
        try {
            // Usa la tabla 'recharges' para estudiantes
            const { data, error } = await supabase
                .from('recharges')
                .select('*')
                .eq('student_id', selectedStudentId)
                .gte('created_at', startDate.toISOString())
                .lte('created_at', new Date(endDate.setHours(23, 59, 59)).toISOString())
                .order('created_at', { ascending: false });

            if (error) throw error;
            setRecharges(data || []);
        } catch (error) {
            console.error("Error fetching student recharges:", error);
            alert("Error al cargar historial: " + error.message);
        } finally {
            setLoading(false);
        }
    }, [selectedStudentId, startDate, endDate]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const totalRecharged = useMemo(() => {
        // Asume que 'amount' en 'recharges' también es numérico (o usa parseFloat si es texto)
        return recharges.reduce((sum, recharge) => sum + (parseFloat(recharge.amount) || 0), 0);
    }, [recharges]);

    if (authLoading) {
        return <div className="flex justify-center items-center h-64"><Loader2 className="animate-spin text-orange-500" size={32} /></div>;
    }

    return (
        <>
            {userType === 'parent' && students.length > 1 && (
                <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Selecciona un Estudiante</label>
                    <select
                        value={selectedStudentId || ''}
                        onChange={(e) => setSelectedStudentId(e.target.value)}
                        className="w-full max-w-xs p-2 border rounded-md bg-white shadow-sm"
                    >
                        {students.map(s => (
                            <option key={s.id} value={s.id}>{s.first_name} {s.first_last_name}</option>
                        ))}
                    </select>
                </div>
            )}
            <div className="flex flex-wrap items-center gap-4 mb-6 p-4 bg-white rounded-lg shadow-sm">
                <label className="text-sm font-medium text-gray-700">Desde:</label>
                <DatePicker selected={startDate} onChange={(date) => setStartDate(date)} className="p-2 border rounded-md" dateFormat="dd/MM/yyyy" />
                <label className="text-sm font-medium text-gray-700">Hasta:</label>
                <DatePicker selected={endDate} onChange={(date) => setEndDate(date)} className="p-2 border rounded-md" dateFormat="dd/MM/yyyy" />
            </div>

            {loading ? (
                <div className="flex justify-center items-center h-64"><Loader2 className="animate-spin text-orange-500" size={32} /></div>
            ) : recharges.length === 0 ? (
                <p className="text-center text-gray-500 py-8">No hay recargas en el rango de fechas seleccionado.</p>
            ) : (
                <div className="space-y-4">
                    <div className="p-4 bg-green-50 text-green-700 rounded-lg font-semibold">
                        Total Recargado en este período: ${totalRecharged.toFixed(2)}
                    </div>
                    {recharges.map(recharge => (
                        <div key={recharge.id} className="bg-white p-4 rounded-lg shadow-sm flex justify-between items-center">
                            <div className="flex items-center gap-4">
                                <div className="p-3 bg-green-100 rounded-full text-green-600">
                                    <TrendingUp size={20} />
                                </div>
                                <div>
                                    <p className="font-semibold text-gray-800">{recharge.notes || 'Recarga de Saldo'}</p>
                                    <p className="text-sm text-gray-500">
                                        {new Date(recharge.created_at).toLocaleDateString('es-SV', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                    </p>
                                </div>
                            </div>
                            <span className="font-bold text-lg text-green-600">
                                +${(parseFloat(recharge.amount) || 0).toFixed(2)}
                            </span>
                        </div>
                    ))}
                </div>
            )}
        </>
    );
};

// --- Sub-componente para historial de Maestro (SIMPLIFICADO y SIN LOGS) ---
const TeacherRechargeHistory = () => {
    const { userData } = useAuth();
    const [recharges, setRecharges] = useState([]);
    const [loading, setLoading] = useState(false);
    const [startDate, setStartDate] = useState(new Date(new Date().setDate(new Date().getDate() - 30)));
    const [endDate, setEndDate] = useState(new Date());

    const fetchData = useCallback(async () => {
        if (!userData?.id) {
            setRecharges([]);
            return;
        }
        setLoading(true);
        try {
            const finalEndDate = new Date(endDate);
            finalEndDate.setHours(23, 59, 59);

            const { data, error } = await supabase
                .from('recharge_teachers')
                .select('*')
                .eq('teacher_id', userData.id)
                .gte('created_at', startDate.toISOString())
                .lte('created_at', finalEndDate.toISOString())
                .order('created_at', { ascending: false });

            if (error) throw error;
            setRecharges(data || []);
        } catch (error) {
            console.error("Error fetching teacher recharges:", error); // Mantenemos el log de errores
            alert("Error al cargar historial: " + error.message);
        } finally {
            setLoading(false);
        }
    }, [userData, startDate, endDate]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // --- CÁLCULO DEL TOTAL (SIMPLIFICADO) ---
    const totalRecharged = useMemo(() => {
        // Sumamos directamente, asumiendo que amount es numérico
        return recharges.reduce((sum, recharge) => sum + (recharge.amount || 0), 0);
    }, [recharges]);
    // --- FIN SIMPLIFICACIÓN ---

    return (
        <>
            <div className="flex flex-wrap items-center gap-4 mb-6 p-4 bg-white rounded-lg shadow-sm">
                <label className="text-sm font-medium text-gray-700">Desde:</label>
                <DatePicker selected={startDate} onChange={(date) => setStartDate(date)} className="p-2 border rounded-md" dateFormat="dd/MM/yyyy" />
                <label className="text-sm font-medium text-gray-700">Hasta:</label>
                <DatePicker selected={endDate} onChange={(date) => setEndDate(date)} className="p-2 border rounded-md" dateFormat="dd/MM/yyyy" />
            </div>

            {loading ? (
                <div className="flex justify-center items-center h-64"><Loader2 className="animate-spin text-orange-500" size={32} /></div>
            ) : recharges.length === 0 ? (
                <p className="text-center text-gray-500 py-8">No hay recargas en el rango de fechas seleccionado.</p>
            ) : (
                <div className="space-y-4">
                    <div className="p-4 bg-green-50 text-green-700 rounded-lg font-semibold">
                        Total Recargado en este período: ${totalRecharged.toFixed(2)}
                    </div>
                    {recharges.map(recharge => (
                        <div key={recharge.id} className="bg-white p-4 rounded-lg shadow-sm flex justify-between items-center">
                            <div className="flex items-center gap-4">
                                <div className="p-3 bg-green-100 rounded-full text-green-600">
                                    <TrendingUp size={20} />
                                </div>
                                <div>
                                    <p className="font-semibold text-gray-800">{recharge.notes || 'Recarga de Saldo'}</p>
                                    <p className="text-sm text-gray-500">
                                        {new Date(recharge.created_at).toLocaleDateString('es-SV', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                    </p>
                                </div>
                            </div>
                            {/* --- MOSTRAR MONTO (SIMPLIFICADO) --- */}
                            {/* Usamos directamente amount || 0 */}
                            <span className="font-bold text-lg text-green-600">
                                +${(recharge.amount || 0).toFixed(2)}
                            </span>
                            {/* --- FIN SIMPLIFICACIÓN --- */}
                        </div>
                    ))}
                </div>
            )}
        </>
    );
};


// --- Componente Principal (Switcher) ---
const HistorialRecargas = () => {
    const { userType, loading } = useAuth();

    if (loading) {
         return <div className="flex justify-center items-center h-64"><Loader2 className="animate-spin text-orange-500" size={32} /></div>;
    }

    return (
        <div className="space-y-6">
            <h1 className="text-3xl font-bold text-gray-800">Historial de Recargas</h1>

            {userType === 'teacher' && <TeacherRechargeHistory />}

            {(userType === 'student' || userType === 'parent') && <StudentRechargeHistory />}

            {userType !== 'teacher' && userType !== 'student' && userType !== 'parent' && (
                <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg text-center">
                    <AlertTriangle className="mx-auto h-12 w-12 text-yellow-400" />
                    <h3 className="mt-2 text-lg font-medium text-yellow-800">Vista no disponible</h3>
                    <p className="mt-1 text-sm text-yellow-700">
                      El historial de recargas solo está disponible para estudiantes, padres o maestros.
                    </p>
                </div>
            )}
        </div>
    );
};

export default HistorialRecargas;